import { assign, isNotEmpty, toRegExp, toString } from '@rehaje/shared'

import { UnknownSchema } from '../../constants'
import {
  SchemaFormatError,
  SchemaMaxLengthError,
  SchemaMinLengthError,
  SchemaPatternError,
  SchemaTransformError
} from '../../errors'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type { RuntimeSchemaSpecific, SchemaStringSpecific } from '../../types'
import type { Formats } from './format.template'

const formatsStore: Formats = {}

export class SchemaStringDefinition extends SchemaDefinition {
  /**
   * 获取校验格式
   */
  static getFormats() {
    return formatsStore
  }

  /**
   * 设置校验格式
   * @param formats 校验格式
   *
   * @example
   * SchemaStringDefinition.setFormats({
   *   timestamp: /^(?:1[0-9]|2[01])\d{11}$/
   * })
   */
  static setFormats(formats: Formats) {
    assign(formatsStore, formats)
  }

  /**
   * 校验值是否符合格式
   * @param value 值
   * @param format 格式
   */
  static testFormat(value: any, format: string) {
    return formatsStore[format]?.test(value) ?? true
  }

  override defaultsMerge(
    value: any,
    schema: SchemaStringSpecific,
    initialize: boolean
  ) {
    if (initialize) {
      if (value === undefined) return schema.default
      return value
    }
    if (schema.required && value === undefined) {
      return schema.default
    }
    return value
  }

  override getIn(schema: SchemaStringSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target: any,
    schema?: SchemaStringSpecific
  ): SchemaStringSpecific {
    return {
      ...schema,
      type: 'Schema.String',
      value: target
    }
  }

  override inferOnlySchema(schema: SchemaStringSpecific) {
    return schema
  }

  override inferTypeScript(schema: SchemaStringSpecific): string {
    const value = schema.value
    if (value !== undefined && this.test(value, schema))
      return JSON.stringify(value)
    return 'string'
  }

  override prune(target: any): any {
    return target
  }

  override transform(target: any, schema: SchemaStringSpecific) {
    if (target === null && schema.nullable) return target
    return this.transformToString(target, schema)
  }

  transformToString(value: any, schema: SchemaStringSpecific) {
    if (
      value === undefined ||
      value === null ||
      (typeof value === 'number' && isNaN(value)) ||
      (typeof value === 'object' && value.toString === toString)
    ) {
      if (value !== schema.fallback)
        this.raise(new SchemaTransformError(schema.name))
      return schema.fallback === undefined ? '' : schema.fallback
    }
    return String(value)
  }

  override validate(target: any, schema: any): void
  override validate(
    target: any,
    schema: RuntimeSchemaSpecific<SchemaStringSpecific>
  ): void {
    if (this.validateMetadata(target, schema, 'string', isNotEmpty)) return
    if (
      schema.format &&
      !SchemaStringDefinition.testFormat(target, schema.format)
    ) {
      this.raise(new SchemaFormatError(schema.name, schema.format))
    }
    if (schema.pattern && !toRegExp(schema.pattern).test(target)) {
      this.raise(new SchemaPatternError(schema.name, schema.pattern))
    }
    if (schema.minLength && target.length < schema.minLength) {
      this.raise(new SchemaMinLengthError(schema.name, schema.minLength))
    }
    if (schema.maxLength && target.length > schema.maxLength) {
      this.raise(new SchemaMaxLengthError(schema.name, schema.maxLength))
    }
  }
}
